// The INTERNAL tap page for the Guv POP Tag (Session AMZ, then called "Tapping For Tracking"; renamed Session AND). The motor-room sticker
// resolves here; the page records the tap and shows the engineer that it worked.
//
// WHY THE PAGE DOES THE RECORDING. The browser's own GET of this URL writes nothing. Recording is a
// POST this page makes on open, because rfidSendMessage puts tag URLs into WhatsApp messages and
// mailto bodies, and every one of those clients fetches a URL to build a preview. A URL that
// recorded on GET would have logged attendance the moment the office SENT the tag to the engineer.
//
// WHAT IT SHOWS, and it is deliberately almost nothing: the lift's own reference and the time just
// recorded. No site, no customer, no address, no history, no compliance state. A stranger who gets
// into a plant room and photographs the sticker learns one opaque asset reference.
//
// House rules: plain English for someone standing at a lift, no double hyphens, no personal names
// beyond the signed-in engineer's own display name (which is their own, shown back to them).
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type PublicTapResult } from "../../lib/api";
import { FullScreenLoader } from "../../components/Loader";
import { EngineerLogVisit } from "./EngineerLogVisit";

// The recorded instant, rendered as London wall clock. A UTC time would read an hour early all
// summer, on the one number this whole page exists to show.
function londonTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  });
}

function londonDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/London",
  });
}

export function AttendanceTapPage() {
  const { token = "" } = useParams();
  const [result, setResult] = useState<PublicTapResult | null>(null);
  const [failed, setFailed] = useState(false);
  // React runs effects twice in development StrictMode. The server's three-minute collapse window
  // makes a second POST harmless (it returns the same recorded tap), but there is no reason to send
  // one, and guarding here keeps the development console honest about how many taps a visit makes.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) {
      if (!token) setFailed(true);
      return;
    }
    sent.current = true;
    let live = true;
    api.publicTap
      .record(token)
      .then((res) => {
        if (live) setResult(res);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [token]);

  if (failed) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-ele-bg px-4 text-ele-body">
        <div className="w-full max-w-md rounded-lg border border-ele-purple-accent bg-black/20 p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">This tag is not in use</h1>
          {/* Says nothing about whether the token ever existed: a retired sticker, a never-issued
              token and a fabricated one all land here reading identically. */}
          <p className="text-ele-body">Please contact the office.</p>
        </div>
      </div>
    );
  }

  if (!result) return <FullScreenLoader />;

  // Session AOE. The first tap since this tag was programmed or rotated is the check that it reads,
  // not a visit, and the platform records it that way. `=== true` rather than a truthy branch: the
  // field is optional on the wire, because the SPA and the API deploy as separate services and a new
  // bundle can reach a server that predates it. Absent degrades to the ordinary attendance page,
  // which is exactly what that older server actually recorded.
  const confirmed = result.isInstallConfirmation === true;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-ele-bg px-4 py-10 text-ele-body">
      <div className="w-full max-w-md rounded-lg border border-ele-purple-accent bg-black/20 p-6 text-center">
        {/* Never "Attendance recorded" on a confirmation. The row is deliberately left out of every
            count, so claiming a visit would promise the office a number the record does not carry,
            and would tell an engineer standing at a lift they had signed in for a visit they have
            not started. */}
        <p className="font-heading text-sm uppercase tracking-widest text-ele-blue">
          {confirmed ? "Sticker confirmed working" : "Attendance recorded"}
        </p>

        <h1 className="mt-3 font-heading text-3xl text-ele-white">{result.assetRef}</h1>

        <p className="mt-6 font-heading text-5xl text-ele-green">{londonTime(result.tappedAt)}</p>
        <p className="mt-1 text-sm text-ele-body">{londonDate(result.tappedAt)}</p>

        {confirmed ? (
          // Says plainly what this tap did and did not do, and what happens next, so nobody taps
          // again looking for the visit to appear. Positive, not a warning: fitting the tag and
          // checking it reads is the job being done correctly.
          <p className="mt-4 text-sm text-ele-body opacity-80">
            This tag is now live. That first tap confirms it reads and is not counted as a visit. The next tap records a
            visit as normal.
          </p>
        ) : null}

        {result.alreadyRecorded && !confirmed ? (
          // Says what actually happened rather than implying a second visit was logged. Two engineers
          // at one lift is an ordinary day, so the second one must not be told "your earlier tap":
          // it may not be theirs, and the page never learns (or shows) whose it is.
          <p className="mt-4 text-sm text-ele-body opacity-80">
            {result.recordedUnderAnotherName
              ? "This lift was already tapped moments ago, by somebody else. Your attendance is covered by that record."
              : "This visit was already recorded. Your earlier tap is the one on file."}
          </p>
        ) : null}

        {/* THE WHOLE IDENTITY BLOCK IS SUPPRESSED ON A CONFIRMATION, and that is a decision rather
            than a simplification. Every line in it is about a VISIT: who it was recorded against,
            and an offer to sign in so the NEXT one carries a name. There is no visit here, so
            "Recorded without a name. Please sign the log card as usual." would send somebody who has
            just fitted a sticker to sign a log card for work they have not done, and the sign-in
            offer would solve a problem this tap does not have (a confirmation is excluded from every
            count whoever made it, so naming it changes nothing). Their next tap is a real
            attendance, and that page makes both offers in full. */}
        {confirmed ? null : (
        <div className="mt-6 border-t border-ele-purple-accent pt-4 text-sm">
          {result.signedInAs ? (
            <p className="text-ele-body">
              Recorded against <span className="text-ele-white">{result.signedInAs}</span>.
            </p>
          ) : (
            <>
              {/* Not a warning and not an error. A tap proves WHEN somebody attended; the log card's
                  signature is what proves who, and it always has been. */}
              <p className="text-ele-body">
                {result.recordedUnderAnotherName
                  ? "Recorded under the other engineer's name. Please sign the log card as usual."
                  : "Recorded without a name. Please sign the log card as usual."}
              </p>
              <div className="mt-2">
                <EngineerLogVisit token={token} surface="tap" />
              </div>
            </>
          )}
        </div>
        )}

        {/* The log-card instruction goes with the visit, so a confirmation gets the instruction that
            actually applies to it: put the sticker somewhere it will still be readable next year. */}
        <p className="mt-6 text-xs text-ele-body opacity-70">
          {confirmed
            ? "Fix the sticker beside the log card so it stays readable, and carry on as normal."
            : "Please complete and sign the log card in the motor room as normal."}
        </p>
      </div>
    </div>
  );
}
